import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo, useState} from 'react'
import {getEntityCompositeID, sortFindingsBySeverity} from './entity-helpers'
import type {FindingEntity} from './types'

const ignoreFindingIds = async (basePath: string, compositeIds: string[]): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'POST',
    body: {entity_ids: compositeIds},
  })

  if (!response.ok) {
    throw new Error(`Failed to ignore findings (status ${response.status} ${response.statusText})`)
  }

  return response
}

const unignoreFindingIds = async (basePath: string, compositeIds: string[]): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'DELETE',
    body: {entity_ids: compositeIds},
  })

  if (!response.ok) {
    throw new Error(`Failed to unignore findings (status ${response.status} ${response.statusText})`)
  }

  return response
}

export function useIgnoreFindings(findings: FindingEntity[], pullRequestBasePath: string, ignoredEntityIds: string[]) {
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(() => new Set(ignoredEntityIds))

  const handleDismiss = useCallback(
    async (findingsToDismiss: FindingEntity[]) => {
      const ids = findingsToDismiss.map(finding => getEntityCompositeID(finding))
      setIgnoredIds(prev => {
        const next = new Set(prev)
        for (const id of ids) next.add(id)
        return next
      })
      try {
        await ignoreFindingIds(pullRequestBasePath, ids)
      } catch {
        setIgnoredIds(prev => {
          const next = new Set(prev)
          for (const id of ids) next.delete(id)
          return next
        })
      }
    },
    [pullRequestBasePath],
  )

  const handleReopen = useCallback(
    async (findingsToReopen: FindingEntity[]) => {
      const ids = findingsToReopen.map(finding => getEntityCompositeID(finding))
      setIgnoredIds(prev => {
        const next = new Set(prev)
        for (const id of ids) next.delete(id)
        return next
      })
      try {
        await unignoreFindingIds(pullRequestBasePath, ids)
      } catch {
        setIgnoredIds(prev => {
          const next = new Set(prev)
          for (const id of ids) next.add(id)
          return next
        })
      }
    },
    [pullRequestBasePath],
  )

  const openFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => !ignoredIds.has(getEntityCompositeID(f)))),
    [findings, ignoredIds],
  )
  const ignoredFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => ignoredIds.has(getEntityCompositeID(f)))),
    [findings, ignoredIds],
  )

  return {openFindings, ignoredFindings, handleDismiss, handleReopen}
}
